// Decision Path Narrative Database
// Maps each node ID to a human-readable description and narrative connector

const PATH_NARRATIVES = {
  // Entry
  S0_ENTRY: {
    label: 'Scenario Briefing',
    description: 'You received a parent concern about a student feeling embarrassed in class. Ms. Ortega is aware.',
    connector: 'With this context in mind, you entered the room.',
  },
  S0_BEGIN: {
    label: 'Conversation Begins',
    description: 'You initiated the conversation with Ms. Ortega.',
    connector: 'She looked up, clearly expecting this meeting.',
  },

  // Stage 1 — Opening
  S1_MA_OPEN: {
    label: 'Ms. Ortega Opens',
    description: 'Ms. Ortega acknowledged the situation: "I\'m guessing this is about the parent email."',
    connector: 'You chose your opening move.',
  },
  S1_O1: {
    label: 'Inquiry First Opening',
    description: 'You led with curiosity: "Before we decide what this requires, I want to hear how you experienced the moment."',
    connector: 'Her shoulders loosened. She was willing to talk.',
    quality: 'strong',
  },
  S1_O2: {
    label: 'Complaint-First Opening',
    description: 'You named the complaint directly: "I need to talk to you about a parent complaint that raised concerns."',
    connector: 'She stiffened, asking what version of events she was responding to.',
    quality: 'mixed',
  },
  S1_O3: {
    label: 'Reassurance Opening',
    description: 'You softened the entry: "I\'m aware a concern came in, and I don\'t want to jump to conclusions. I wanted to check in with you first."',
    connector: 'She was willing to talk, but unclear on the purpose.',
    quality: 'mixed',
  },
  S1_O4: {
    label: 'Standards-First Opening',
    description: 'You led with optics: "We need to be careful here because families are already paying attention."',
    connector: 'She broke eye contact, reading this as a threat.',
    quality: 'weak',
  },

  // Stage 2 — Fact-Finding
  S2_OPEN: {
    label: 'Open Fact-Finding',
    description: 'Ms. Ortega was frustrated but willing to explain her side of the story.',
    connector: 'You had a window to gather context.',
  },
  S2_GUARDED: {
    label: 'Guarded Fact-Finding',
    description: 'Ms. Ortega was braced, unsure if she was being asked to explain or defend herself.',
    connector: 'Your next move would set the tone.',
  },
  S2_DEF: {
    label: 'Defensive Fact-Finding',
    description: 'Ms. Ortega perceived this as a complaint-driven conversation and became wary.',
    connector: 'Trust was fragile. You pressed on.',
  },
  S2_ESC: {
    label: 'Escalating Fact-Finding',
    description: 'Ms. Ortega felt the family\'s reaction was being prioritized over her perspective.',
    connector: 'The temperature in the room was rising.',
  },
  S2_O1: {
    label: 'Deep Inquiry',
    description: 'You asked her to walk you through the moment as she experienced it.',
    connector: 'She described the interaction honestly, acknowledging it may have landed harder than intended.',
    quality: 'strong',
  },
  S2_O2: {
    label: 'Balanced Inquiry',
    description: 'You shared the parent\'s concern and asked for her side: "Help me understand your perspective."',
    connector: 'She acknowledged the gap between intent and impact.',
    quality: 'mixed',
  },
  S2_O3: {
    label: 'Premature Judgment',
    description: 'You stated the concern was serious and needed immediate attention, regardless of intent.',
    connector: 'She felt judged before being heard—the meaning was decided without context.',
    quality: 'weak',
  },
  S2_O4: {
    label: 'Vague Reassurance',
    description: 'You expressed concern without specifics: "I don\'t want to overreact, but this obviously upset a family."',
    connector: 'She was confused about what exactly she was supposed to respond to.',
    quality: 'mixed',
  },

  // Stage 3 — Naming
  S3_CLEAR: {
    label: 'Clear Entry',
    description: 'Enough context had been gathered. The conversation was in a workable place.',
    connector: 'It was time to name the issue.',
  },
  S3_TENSE: {
    label: 'Tense Entry',
    description: 'Blame sensitivity was high. The issue still needed to be named carefully.',
    connector: 'How you framed it next would matter deeply.',
  },
  S3_BLURRY: {
    label: 'Blurry Entry',
    description: 'The issue was still soft and undefined. Clarity was missing.',
    connector: 'You attempted to name what happened.',
  },
  S3_ESCALATING: {
    label: 'Escalating Entry',
    description: 'Trust was low. The conversation was heading toward adversarial territory.',
    connector: 'Your next words would either de-escalate or confirm her fears.',
  },
  S3_O1: {
    label: 'Named Student Impact',
    description: 'You clearly named the student\'s experience: "I want to be clear about the student impact that\'s being reported, even as we sort out the full context."',
    connector: 'She agreed — she didn\'t want a student leaving class feeling shut down.',
    quality: 'strong',
  },
  S3_O2: {
    label: 'Labeled as Management Problem',
    description: 'You framed it as a classroom-management issue needing immediate correction.',
    connector: 'She pushed back: "That\'s a big leap from one parent complaint."',
    quality: 'weak',
  },
  S3_O3: {
    label: 'Vague Naming',
    description: 'You avoided blame but stayed vague: "Something landed poorly."',
    connector: 'She was unclear what she was expected to own.',
    quality: 'mixed',
  },
  S3_O4: {
    label: 'Equity-Aware Inquiry',
    description: 'You explored whether context, tone, or prior trust issues shaped the experience.',
    connector: 'She leaned in—this family had been sensitive to tone before. Context mattered.',
    quality: 'strong',
  },

  // Stage 4 — Resolution
  S4_PRODUCTIVE: {
    label: 'Productive Resolution',
    description: 'Trust and clarity were both workable. The conversation could move toward next steps.',
    connector: 'You proposed a path forward.',
  },
  S4_FRAGILE: {
    label: 'Fragile Resolution',
    description: 'Trust was low. Any resolution would feel imposed rather than shared.',
    connector: 'You attempted to close the loop.',
  },
  S4_BLURRY: {
    label: 'Blurry Resolution',
    description: 'The issue remained under-defined. Resolution would be difficult without shared clarity.',
    connector: 'You tried to land on next steps.',
  },
  S4_O1: {
    label: 'Collaborative Follow-Up',
    description: 'You proposed agreeing on one next step that addresses the concern and gives better context.',
    connector: 'She relaxed: "I can work with that — better than circling the complaint."',
    quality: 'strong',
  },
  S4_O2: {
    label: 'Directive Demand',
    description: 'You demanded immediate adjustment: "I need you to adjust this immediately."',
    connector: 'She complied flatly. No ownership. No learning.',
    quality: 'weak',
  },
  S4_O3: {
    label: 'Deferred Resolution',
    description: 'You suggested leaving it for now and thinking about next steps later.',
    connector: 'She nodded, uncertain. The issue remained unresolved.',
    quality: 'weak',
  },
  S4_O4: {
    label: 'Shared Ownership',
    description: 'You asked what follow-up would feel fair while still addressing the concern.',
    connector: 'She proposed a follow-up observation and debrief—a genuinely collaborative resolution.',
    quality: 'strong',
  },

  // Stage 5 — Closure
  S5_STRONG: {
    label: 'Strong Closure',
    description: 'The room was workable. Both parties had a shared understanding.',
    connector: 'You wrapped up the conversation.',
  },
  S5_MIXED: {
    label: 'Mixed Closure',
    description: 'The path forward was not solid. Uncertainty lingered.',
    connector: 'You attempted a closing statement.',
  },
  S5_WEAK: {
    label: 'Weak Closure',
    description: 'Trust was compromised. The closure would feel hollow.',
    connector: 'You closed the conversation.',
  },
  S5_O1: {
    label: 'Appreciative Close',
    description: 'You thanked her for walking through this together and affirmed the plan.',
    connector: 'She appreciated the approach and committed to the agreed-upon steps.',
    quality: 'strong',
  },
  S5_O2: {
    label: 'Directive Close',
    description: 'You closed with a warning: "Just make sure it doesn\'t happen again."',
    connector: 'She responded flatly. Compliance without buy-in.',
    quality: 'weak',
  },
  S5_O3: {
    label: 'Vague Close',
    description: 'You said you\'d think about next steps, leaving the resolution unclear.',
    connector: 'She nodded, uncertain about what would happen next.',
    quality: 'mixed',
  },

  // Terminal
  OUTCOME_ROUTER: {
    label: 'Outcome Calculated',
    description: 'The conversation ended. Your leadership instruments tell the story.',
    connector: null,
  },
};

export function getPathNarrative(nodeId) {
  return PATH_NARRATIVES[nodeId] || {
    label: nodeId,
    description: 'Decision point in the conversation.',
    connector: null,
  };
}

export default PATH_NARRATIVES;
